import { Component, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges, inject } from '@angular/core';
import { AbstractControl } from '@angular/forms';

import { DsHelpGroup, DsHelpItem } from '@frontend/ui/help-group';
import { CommonMessages, WindowEvent } from '@frontend/vanilla/core';
import { TrustAsHtmlPipe } from '@frontend/vanilla/shared/browser';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Subscription, debounceTime, tap } from 'rxjs';

import { ValidationConfig } from './validation.client-config';

/**
 * @whatItDoes Provides a way to display form element's validation messages
 *
 * @stable
 */
@Component({
    imports: [TrustAsHtmlPipe, DsHelpGroup, DsHelpItem, IconCustomComponent],
    selector: 'lh-validation-messages',
    templateUrl: 'validation-messages.component.html',
})
export class ValidationMessagesComponent implements OnInit, OnChanges, OnDestroy {
    @Input() showOnPristine: boolean;
    @Input() showOnEdit: boolean;
    @Input() useDS = false;
    @Input() showOnSuccess = false;
    @Input() messages: any;
    @Input() formElement: AbstractControl;
    @Input() formElementRef: any;
    @Input() triggerValidationsOnChange: boolean;
    @Input() debounceTimeMs: number | null;

    focusListener: Function;
    blurListener: Function;

    private errorMessages: { [key: string]: string };
    public focused = false;
    public debouncedInteraction = false;
    private fieldValueListner: Subscription;

    private commonMessages = inject(CommonMessages);
    private validationConfig = inject(ValidationConfig);
    private renderer = inject(Renderer2);

    ngOnInit() {
        this.initializeValueChangeListener();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.messages?.currentValue) {
            this.setMessages(changes.messages.currentValue);
        }

        if (changes.formElementRef?.currentValue?.addEventListener && !this.triggerValidationsOnChange) {
            this.focusListener = this.renderer.listen(this.formElementRef, WindowEvent.Focus, () => this.setFocused(true));
            this.blurListener = this.renderer.listen(this.formElementRef, WindowEvent.Blur, () => this.setFocused(false));
        }
    }

    ngOnDestroy() {
        if (this.focusListener) {
            this.focusListener();
        }
        if (this.blurListener) {
            this.blurListener();
        }
        if (this.fieldValueListner) {
            this.fieldValueListner.unsubscribe();
        }
    }

    private initializeValueChangeListener(): void {
        if (!this.formElement) return;

        const time = this.debounceTimeMs ?? 0;

        this.fieldValueListner = this.formElement.valueChanges
            .pipe(
                tap(() => (this.debouncedInteraction = false)),
                debounceTime(this.triggerValidationsOnChange && time > 0 ? time : 0),
                tap(() => {
                    if (this.triggerValidationsOnChange) {
                        this.debouncedInteraction = this.formElement.dirty;
                    }
                }),
            )
            .subscribe();
    }

    showErrors(): boolean {
        if (!this.hasErrors()) {
            return false;
        }
        // showOnPristine (show always)
        if (this.showOnPristine) {
            return true;
        }

        // showOnEdit
        if (this.showOnEdit && this.formElement.dirty) {
            return true;
        }

        // default mode (blurred and touched)
        return !this.showOnEdit && this.isInteracted();
    }

    showSuccess(): boolean {
        if (!this.formElement) {
            return false;
        }
        return this.showOnSuccess && this.formElement.valid && this.isInteracted();
    }

    isInteracted(): boolean {
        if (!this.formElement) {
            return false;
        }

        if (this.triggerValidationsOnChange) {
            return this.debounceTimeMs && this.debounceTimeMs > 0 ? this.debouncedInteraction : this.formElement.dirty;
        }

        return !this.focused && this.formElement.touched;
    }

    getMessage(): string | null {
        if (this.formElement && !this.formElement.valid) {
            if (this.errorMessages) {
                for (const key in this.formElement.errors) {
                    let mapping: string | null = null;

                    if ((this.formElement as any).errorMapping) {
                        mapping = (this.formElement as any).errorMapping[key];
                    }

                    const error = mapping || key;
                    mapping = this.validationConfig.errorMapping[error] || error;

                    const errorMessage = this.errorMessages[mapping.toLowerCase()];

                    if (errorMessage) {
                        return errorMessage;
                    }
                }

                if (this.errorMessages[CommonMessages.GeneralValidationErrorKey.toLowerCase()] !== undefined) {
                    return this.errorMessages[CommonMessages.GeneralValidationErrorKey.toLowerCase()] || null;
                }
            }
            return this.commonMessages[CommonMessages.GeneralValidationErrorKey] || null;
        }
        return null;
    }

    setFocused(isFocused: boolean) {
        this.focused = isFocused;
    }

    private hasErrors(): boolean {
        return this.formElement?.errors ? Object.keys(this.formElement.errors).length > 0 : false;
    }

    private setMessages(messages: any) {
        this.errorMessages = {};
        for (const key in messages) {
            this.errorMessages[key.toLowerCase()] = messages[key];
        }
    }
}
